import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, BookOpen, FileUp, Sparkles } from "lucide-react";
import { priorityMeta } from "@/lib/constants";

const STORY_STATUS = [
  { key: "backlog", label: "Backlog", color: "#9CA3AF" },
  { key: "refinement", label: "Refinement", color: "#8B5CF6" },
  { key: "ready", label: "Ready", color: "#F59E0B" },
  { key: "in_progress", label: "In progress", color: "#10B981" },
  { key: "testing", label: "Testing", color: "#0EA5E9" },
  { key: "demo", label: "Demo", color: "#EC4899" },
  { key: "released", label: "Released", color: "#14B8A6" },
  { key: "done", label: "Done", color: "#2563EB" },
];
const statusMetaOf = (key) => STORY_STATUS.find((s) => s.key === key) || { key, label: key, color: "#64748B" };
const blank = { title: "", description: "", priority: "medium", points: "", status: "backlog", linked_task_id: "", sprint: "", owner: "", display_id: "" };

export default function StoryBacklog({ projectId, tasks = [] }) {
  const [stories, setStories] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get(`/projects/${projectId}/stories`).then(({ data }) => setStories(data)).catch(() => setStories([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...blank, ...s, points: s.points ?? "", linked_task_id: s.linked_task_id || "", sprint: s.sprint || "", owner: s.owner || "", display_id: s.display_id || "" }); setEditId(s.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, points: form.points === "" ? null : Number(form.points), linked_task_id: form.linked_task_id || null };
      if (editId) await api.put(`/stories/${editId}`, payload);
      else await api.post(`/projects/${projectId}/stories`, payload);
      setOpen(false);
      toast.success(editId ? "Story updated" : "Story added");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/stories/${id}`);
    setOpen(false);
    toast.success("Story deleted");
    load();
  };

  const setStatus = async (s, status) => {
    if (status === "__custom") {
      const custom = window.prompt("Name this status (e.g. UAT, Blocked, Deferred):", "");
      if (!custom || !custom.trim()) return;
      status = custom.trim().toLowerCase().replace(/\s+/g, "_");
    }
    try {
      const { title, description, priority, points, linked_task_id, sprint, owner, display_id } = s;
      await api.put(`/stories/${s.id}`, { title, description, priority, points: points ?? null, status, linked_task_id: linked_task_id || null, sprint: sprint || "", owner: owner || "", display_id: display_id || null });
      setStories((arr) => arr.map((x) => (x.id === s.id ? { ...x, status } : x)));
    } catch (e) { toast.error("Could not update status"); }
  };

  const stats = useMemo(() => {
    const byStatus = {};
    let pts = 0;
    (stories || []).forEach((s) => { byStatus[s.status] = (byStatus[s.status] || 0) + 1; pts += Number(s.points) || 0; });
    return { byStatus, pts };
  }, [stories]);

  if (stories === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="story-backlog">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="font-display text-lg font-bold">User story backlog <span className="text-sm text-muted-foreground font-normal">({stories.length})</span></h3>
          <div className="flex gap-3 text-xs flex-wrap">
            {Object.entries(stats.byStatus).map(([k, n]) => {
              const st = statusMetaOf(k);
              return <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: st.color }} /> <b>{n}</b> {st.label}</span>;
            })}
            <span className="text-muted-foreground">· {stats.pts} pts total</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)} data-testid="import-stories-button"><FileUp className="h-4 w-4" /> Import from file</Button>
          <Button className="gap-2" onClick={openNew} data-testid="add-story-button"><Plus className="h-4 w-4" /> New story</Button>
        </div>
      </div>

      {stories.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid="stories-empty">
          <BookOpen className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Capture user stories here — prioritize the backlog, size them, and link them to plan tasks.</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 w-20">ID</th>
                <th className="px-3 py-2">Story</th>
                <th className="px-3 py-2 w-24">Sprint</th>
                <th className="px-3 py-2 w-28">Owner</th>
                <th className="px-3 py-2 w-24">Priority</th>
                <th className="px-3 py-2 w-16">Points</th>
                <th className="px-3 py-2 w-36">Status</th>
                <th className="px-3 py-2 w-44">Linked task</th>
              </tr>
            </thead>
            <tbody>
              {stories.map((s) => {
                const pm = priorityMeta(s.priority);
                const linked = tasks.find((t) => t.id === s.linked_task_id);
                return (
                  <tr key={s.id} className="border-t border-border/60 hover:bg-secondary/30 cursor-pointer" onClick={() => openEdit(s)} data-testid={`story-row-${s.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs font-bold text-primary">{s.display_id}</td>
                    <td className="px-3 py-2.5">
                      <p className={`font-medium ${s.status === "done" ? "line-through text-muted-foreground" : ""}`}>{s.title}</p>
                      {s.description && <p className="text-xs text-muted-foreground truncate max-w-md">{s.description}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`story-sprint-${s.id}`}>{s.sprint ? <span className="font-mono font-semibold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">{s.sprint}</span> : "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground truncate" data-testid={`story-owner-${s.id}`}>{s.owner || "—"}</td>
                    <td className="px-3 py-2.5"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span></td>
                    <td className="px-3 py-2.5 tabular-nums">{s.points ?? "—"}</td>
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Select value={s.status} onValueChange={(v) => setStatus(s, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`story-status-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STORY_STATUS.map((st) => (
                            <SelectItem key={st.key} value={st.key}>
                              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: st.color }} /> {st.label}</span>
                            </SelectItem>
                          ))}
                          {!STORY_STATUS.some((st) => st.key === s.status) && (
                            <SelectItem value={s.status}>
                              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-400" /> {s.status}</span>
                            </SelectItem>
                          )}
                          <SelectItem value="__custom"><span className="text-primary font-semibold">+ Name your own…</span></SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground truncate">{linked ? linked.title : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <StoryImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={projectId} onImported={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="story-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit" : "New"} user story</DialogTitle>
            <DialogDescription>As a &lt;user&gt;, I want &lt;goal&gt; so that &lt;benefit&gt;.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="s-title">Story</Label>
              <Input id="s-title" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="As a PM, I want …" data-testid="story-title-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="s-desc">Acceptance criteria / notes</Label>
              <Textarea id="s-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="story-desc-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="s-id">Story ID</Label>
                <Input id="s-id" value={form.display_id} onChange={(e) => set("display_id", e.target.value)} placeholder="US-001 / ACME-42" data-testid="story-id-input" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label htmlFor="s-sprint">Sprint</Label>
                <Input id="s-sprint" value={form.sprint} onChange={(e) => set("sprint", e.target.value)} placeholder="Sprint 4 / S24-07" data-testid="story-sprint-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="s-owner">Owner</Label>
                <Input id="s-owner" value={form.owner} onChange={(e) => set("owner", e.target.value)} placeholder="Who owns it" data-testid="story-owner-input" className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="story-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="s-points">Points</Label>
                <Input id="s-points" type="number" min="0" step="0.5" value={form.points} onChange={(e) => set("points", e.target.value)} data-testid="story-points-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => {
                  if (v === "__custom") {
                    const custom = window.prompt("Name this status (e.g. UAT, Blocked, Deferred):", "");
                    if (custom && custom.trim()) set("status", custom.trim().toLowerCase().replace(/\s+/g, "_"));
                    return;
                  }
                  set("status", v);
                }}>
                  <SelectTrigger className="mt-1.5" data-testid="story-form-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STORY_STATUS.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
                    {!STORY_STATUS.some((st) => st.key === form.status) && form.status && <SelectItem value={form.status}>{form.status}</SelectItem>}
                    <SelectItem value="__custom"><span className="text-primary font-semibold">+ Name your own…</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Linked task (optional)</Label>
              <Select value={form.linked_task_id || "none"} onValueChange={(v) => set("linked_task_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="story-linked-task-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && (
                <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => remove(editId)} data-testid="story-delete-button">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
              <Button type="submit" disabled={saving} data-testid="story-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StoryImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [importing, setImporting] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setChecked({}); };

  const extract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/stories/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.stories);
      setChecked(Object.fromEntries(data.stories.map((_, i) => [i, true])));
      toast.success(`Maestro found ${data.stories.length} stor${data.stories.length === 1 ? "y" : "ies"} — review and import`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read this file"); }
    finally { setExtracting(false); }
  };

  const doImport = async () => {
    const picked = preview.filter((_, i) => checked[i]);
    if (!picked.length) return;
    setImporting(true);
    try {
      for (const s of picked) {
        await api.post(`/projects/${projectId}/stories`, { ...s, status: "backlog", linked_task_id: null });
      }
      toast.success(`${picked.length} stor${picked.length === 1 ? "y" : "ies"} imported with US-IDs`);
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (e) { toast.error("Import failed part-way — check the registry"); onImported?.(); }
    finally { setImporting(false); }
  };

  const allOn = preview && preview.every((_, i) => checked[i]);
  const pickedCount = preview ? preview.filter((_, i) => checked[i]).length : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="story-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileUp className="h-5 w-5 text-primary" /> Import user stories</DialogTitle>
          <DialogDescription>Upload a story table or document — Maestro extracts the stories, you pick which to add. CSV, Excel, Word, PDF, or text.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/50 transition-colors" data-testid="story-import-dropzone">
              <FileUp className="h-7 w-7 text-muted-foreground" />
              <span className="text-sm font-medium">{file ? file.name : "Choose a file"}</span>
              <span className="text-xs text-muted-foreground">.csv · .xlsx · .docx · .pdf · .txt · .md (max 15 MB)</span>
              <input type="file" accept=".csv,.xlsx,.docx,.pdf,.txt,.md,.tsv" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="story-import-file-input" />
            </label>
            <Button className="w-full gap-2" onClick={extract} disabled={!file || extracting} data-testid="story-import-extract">
              {extracting ? <><Loader2 className="h-4 w-4 animate-spin" /> Maestro is reading the file…</> : <><Sparkles className="h-4 w-4" /> Extract stories</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!allOn} onChange={(e) => setChecked(Object.fromEntries(preview.map((_, i) => [i, e.target.checked])))}
                  className="h-4 w-4 accent-[#0044FF]" data-testid="story-import-select-all" />
                Select all ({preview.length})
              </label>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="story-import-back">Choose another file</Button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border max-h-[46vh] overflow-y-auto">
              {preview.map((s, i) => {
                const pm = priorityMeta(s.priority);
                return (
                  <label key={i} className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${checked[i] ? "" : "opacity-50"}`} data-testid={`story-import-row-${i}`}>
                    <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))}
                      className="h-4 w-4 accent-[#0044FF] mt-0.5 shrink-0" data-testid={`story-import-check-${i}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{s.title}</p>
                      {s.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{s.description}</p>}
                    </div>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span>
                    {s.points != null && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-secondary shrink-0">{s.points} pts</span>}
                  </label>
                );
              })}
            </div>
            <Button className="w-full gap-2" onClick={doImport} disabled={!pickedCount || importing} data-testid="story-import-confirm">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Import {pickedCount} stor{pickedCount === 1 ? "y" : "ies"} to the backlog
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
